import type { AbaDaPlanilha } from '@obraplan/core/domain/takeoff/planilha-de-indices';
import {
  abasDosBytes,
  mensagemDaFalha,
  type PedidoDaPlanilha,
  type RespostaDaPlanilha,
} from './abas-do-arquivo';

/**
 * A planilha de serviços aberta para a importação: as abas, ou a frase que a tela mostra.
 *
 * **Toda leitura termina numa das duas.** O defeito de 22/10/2026 foi o contrário disso: a
 * planilha de fundação da Veneza congelava a tela no "Lendo…", e nada dizia que ela não ia
 * abrir. A leitura agora roda num worker com prazo; passou do prazo, o worker é encerrado e
 * a tela diz o que conferir na planilha.
 */
export type LeituraDoArquivo =
  | { readonly ok: true; readonly abas: readonly AbaDaPlanilha[] }
  | { readonly ok: false; readonly mensagem: string };

/**
 * Quanto a leitura pode levar. A maior planilha que passou por aqui (12.674 linhas) abre em
 * meio segundo; um minuto é folga de sobra para máquina lenta, e curto para quem espera.
 */
export const PRAZO_DA_LEITURA_MS = 60_000;

export const MENSAGEM_DE_ARQUIVO_VAZIO = 'O arquivo está vazio.';
export const MENSAGEM_DE_ARQUIVO_SEM_DADO = 'O arquivo não tem nenhuma linha com dado.';
export const MENSAGEM_DO_WORKER_PARADO =
  'A leitura da planilha parou antes de terminar. Recarregue a página e tente de novo; se continuar, a planilha pode ser grande demais para a memória deste navegador.';

export function mensagemDoPrazo(prazoMs: number): string {
  const segundos = Math.round(prazoMs / 1000);
  const quanto =
    segundos % 60 === 0
      ? `${String(segundos / 60)} ${segundos === 60 ? 'minuto' : 'minutos'}`
      : `${String(segundos)} segundos`;
  return (
    `A planilha levou mais de ${quanto} para abrir, e a leitura foi interrompida. ` +
    'Confira se ela não tem linhas ou colunas formatadas até o fim da folha (no Excel, Ctrl+End leva à última célula usada), apague o excesso e salve de novo.'
  );
}

function falha(mensagem: string): LeituraDoArquivo {
  return { ok: false, mensagem };
}

export async function lerAbasDoArquivo(
  file: File,
  prazoMs: number = PRAZO_DA_LEITURA_MS,
): Promise<LeituraDoArquivo> {
  if (file.size === 0) return falha(MENSAGEM_DE_ARQUIVO_VAZIO);

  let bytes: Uint8Array;
  try {
    bytes = new Uint8Array(await file.arrayBuffer());
  } catch (causa) {
    // O arquivo apagado ou movido entre a escolha e a leitura.
    console.error('[obraplan] falha ao abrir a planilha', file.name, causa);
    return falha('Não foi possível abrir este arquivo. Escolha-o de novo.');
  }

  const lida = (await lerNoWorker(bytes, file.name, prazoMs)) ?? lerAqui(bytes, file.name);
  if (lida.ok && lida.abas.every((aba) => aba.rows.length === 0)) {
    return falha(MENSAGEM_DE_ARQUIVO_SEM_DADO);
  }
  return lida;
}

/** Onde worker não há — o teste, o navegador que o recusa —, a leitura é aqui mesmo. */
function lerAqui(bytes: Uint8Array, nome: string): LeituraDoArquivo {
  try {
    return { ok: true, abas: abasDosBytes(bytes, nome) };
  } catch (causa) {
    console.error('[obraplan] falha ao ler a planilha de serviços', nome, causa);
    return falha(mensagemDaFalha(causa));
  }
}

/**
 * A leitura no worker, com prazo. `undefined` é "não há worker aqui": quem chama lê na thread.
 *
 * Nunca rejeita: a resposta, o erro do worker, a mensagem que não atravessa e o prazo vencido
 * resolvem a promessa uma vez só, e o worker é encerrado em todos eles.
 */
function lerNoWorker(
  bytes: Uint8Array,
  nome: string,
  prazoMs: number,
): Promise<LeituraDoArquivo | undefined> {
  if (typeof Worker === 'undefined') return Promise.resolve(undefined);

  let worker: Worker;
  try {
    worker = new Worker(new URL('./ler-planilha.worker.ts', import.meta.url), { type: 'module' });
  } catch {
    return Promise.resolve(undefined);
  }

  return new Promise((resolve) => {
    let respondeu = false;
    const relogio = setTimeout(() => {
      responder(falha(mensagemDoPrazo(prazoMs)));
    }, prazoMs);

    function responder(leitura: LeituraDoArquivo): void {
      if (respondeu) return;
      respondeu = true;
      clearTimeout(relogio);
      worker.terminate();
      resolve(leitura);
    }

    worker.addEventListener('message', (evento: MessageEvent<RespostaDaPlanilha>) => {
      const resposta = evento.data;
      responder(
        resposta.tipo === 'PRONTO' ? { ok: true, abas: resposta.abas } : falha(resposta.mensagem),
      );
    });
    // O worker que não carrega (o arquivo do worker de uma versão anterior, depois de uma
    // publicação) e o que morre por memória chegam aqui; o que some calado, no relógio.
    worker.addEventListener('error', (evento) => {
      console.error('[obraplan] o worker da planilha parou', nome, evento.message);
      responder(falha(MENSAGEM_DO_WORKER_PARADO));
    });
    worker.addEventListener('messageerror', () => {
      responder(falha(MENSAGEM_DO_WORKER_PARADO));
    });

    try {
      const pedido: PedidoDaPlanilha = { bytes, nome };
      worker.postMessage(pedido, bytes.buffer instanceof ArrayBuffer ? [bytes.buffer] : []);
    } catch (causa) {
      console.error('[obraplan] falha ao mandar a planilha ao worker', nome, causa);
      responder(falha(mensagemDaFalha(causa)));
    }
  });
}
